import { UserRole } from '@textile-pos/types';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useReducer } from 'react';
import { api } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────

interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  tenantId: string;
  tenantName: string;
  agreedToTerms: boolean;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: AuthUser; token: string } }
  | { type: 'AUTH_FAILURE' }
  | { type: 'LOGOUT' };

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, role?: UserRole) => Promise<void>;
  logout: () => void;
  acceptTerms: () => void;
}

// ─── Reducer ──────────────────────────────────────────────────────

const storedUser = localStorage.getItem('auth_user');
const initialState: AuthState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  token: localStorage.getItem('auth_token'),
  isAuthenticated: !!localStorage.getItem('auth_token') && !!storedUser,
  isLoading: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true };
    case 'AUTH_SUCCESS':
      return {
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'AUTH_FAILURE':
    case 'LOGOUT':
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    default:
      return state;
  }
}

// ─── Context ──────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = useCallback(
    async (email: string, password: string, role: UserRole = UserRole.TENANT_ADMIN) => {
      dispatch({ type: 'AUTH_START' });
      try {
        const response = await api.post('/auth/login', { email, password, role });
        const { token, user } = response.data.data;

        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify(user));

        // Auto-configure permission/feature flags for the authenticated tenant shop from their subscription plan
        const flags = user.planFeatures || {
          bulkMatrix:
            user.role === 'SUPER_ADMIN' || user.role === 'TENANT_ADMIN' || user.role === 'MANAGER',
          gstComplianceExport: user.role === 'SUPER_ADMIN' || user.role === 'TENANT_ADMIN',
          loyaltyEngine: user.role === 'SUPER_ADMIN' || user.role === 'TENANT_ADMIN',
        };
        localStorage.setItem(`tenant_flags_${user.tenantId}`, JSON.stringify(flags));

        dispatch({ type: 'AUTH_SUCCESS', payload: { user, token } });
      } catch (err: any) {
        localStorage.removeItem('auth_token');
        dispatch({ type: 'AUTH_FAILURE' });
        throw new Error(err.response?.data?.message || 'Login failed. Invalid email or password.');
      }
    },
    [],
  );

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    dispatch({ type: 'LOGOUT' });
  }, []);

  const acceptTerms = useCallback(() => {
    if (state.user) {
      const updatedUser = { ...state.user, agreedToTerms: true };
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      dispatch({ type: 'AUTH_SUCCESS', payload: { user: updatedUser, token: state.token! } });
    }
  }, [state.user, state.token]);

  const value = useMemo(
    () => ({ ...state, login, logout, acceptTerms }),
    [state, login, logout, acceptTerms],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
